<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { Color, ColorSemantic } from '@previo/codebooks'
import PCard from '../card/PCard.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSemantic, PSpaceSize } from '../space/types'
import { PCardRound } from '../card/types'
import { PMaxLines } from '../typography/types'
import PTextTitle2 from '../typography/title/PTextTitle2.vue'
import PBtn from '../btn/PBtn.vue'
import { PBtnType } from '../btn/types'
import PSlideTransition from '../transition/PSlideTransition.vue'

type Props = {
  title?: null | string
  color?:
    | null
    | Color.WHITE
    | Color.GREY_50
    | ColorSemantic.SURFACE_BACKGROUND
    | ColorSemantic.CARD_ON_WHITE_BACKGROUND
    | ColorSemantic.CARD_ON_GRAY_BACKGROUND
  hideBorder?: boolean
  enableExpand?: boolean
}

const isExpand = defineModel<boolean>('isExpand', { default: true })

const props = withDefaults(defineProps<Props>(), {
  color: Color.WHITE,
  hideBorder: false,
  enableExpand: false,
})

const slots = useSlots()

const hasHeader = computed<boolean>(() => {
  return Boolean(props.title || slots.title || slots.actions || props.enableExpand)
})
</script>

<template>
  <p-card
    :pa-mobile="PSpaceSemantic.CARDS"
    :color="color"
    :round="PCardRound.MD"
    :show-border="!hideBorder"
    class="p-title-card"
  >
    <div v-if="hasHeader" class="ds-title-card-header p-flex p-items-center p-no-wrap">
      <slot name="title">
        <p-text-title2 :text="title" :color="Color.GREY_600" :max-lines="PMaxLines.ONE" />
      </slot>
      <p-space :mobile="PSpaceSize.GROW" />
      <p-space :mobile="PSpaceSize.SM" />

      <slot name="actions" />

      <template v-if="enableExpand">
        <p-space :mobile="PSpaceSize.XS" />
        <p-btn
          :icon-end="isExpand ? 'chevron-up' : 'chevron-down'"
          :type="PBtnType.TERTIARY"
          cy="expand"
          @click="isExpand = !isExpand"
        />
      </template>
    </div>

    <p-slide-transition v-if="props.enableExpand">
      <div v-show="isExpand">
        <p-space v-if="$slots['default']" :mobile="PSpaceSize.SM" vertical />
        <slot name="default" />
      </div>
    </p-slide-transition>

    <template v-else>
      <p-space v-if="hasHeader && $slots['default']" :mobile="PSpaceSize.XS" vertical />
      <slot name="default" />
    </template>
  </p-card>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/mixins';

.ds-title-card-header {
  min-height: 44px;

  @include laptopAndLarge {
    min-height: 36px;
  }
}
</style>
